import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { CreateSupplierOfficeInput } from "@/gql/graphql";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { SupplierOfficeForm } from "@/app/Offices/Forms/SupplierOfficeForm";
import { officeValidation } from "@/app/Offices/Validators/officeValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createSupplierOfficeMutation } from "@/app/Offices/GraphQL/supplierOfficeMutations";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { SupplierOfficeRecord } from "@/app/Offices/GraphQL/supplierOfficeRecordQuery";
import { useTranslations } from "use-intl";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";

type OfficeCreateDialogProps = {
  initialValues?: Partial<CreateSupplierOfficeInput>;
};
export const OfficeCreateDialog: DialogComponent<
  OfficeCreateDialogProps,
  SupplierOfficeRecord
> = ({ initialValues }) => {
  const t = useTranslations("office");
  const { defaultSupplierId } = useGlobalSupplier();
  const { mutateAsync } = useGqlMutation(createSupplierOfficeMutation);
  const { close, resolve } = useDialogState<SupplierOfficeRecord>();

  return (
    <BaseDialog title={t("dialogs.add_office_title")}>
      <GenericForm<CreateSupplierOfficeInput>
        validationSchema={toFormikValidationSchema(officeValidation)}
        onSubmit={async (values) => {
          const res = await mutateAsync({
            input: values,
          });

          resolve?.(res.createSupplierOffice);

          close();
        }}
        initialValues={{
          name: "",
          email: "",
          phone: "",
          code: "",
          pickup_instructions: "",
          city: {
            connect: null!,
          },
          address: {
            upsert: {
              address_line_1: "",
              address_line_2: "",
              city: "",
              state: "",
              country: "",
              postcode: "",
            },
          },
          supplier: {
            connect: defaultSupplierId ?? null!,
          },
          ...initialValues,
        }}
      >
        <SupplierOfficeForm />
        <DialogSubmitButton text={t("actions.add_office")} />
      </GenericForm>
    </BaseDialog>
  );
};
